// React imports
import { useState } from 'react';
import PropTypes from 'prop-types';

// material-ui imports
import { Stack, Typography, Button, CircularProgress } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// material-ui icons imports
import UploadIcon from '@mui/icons-material/Upload';

// project imports
import gestopAPI from 'utils/gestopAPI';
import FileMngrDialog from './FileMngrDialog';
import fileMngrBytesToSize from '../utils/fileMngrBytesToSize';
import { getFileMngrErrorMessage, isFileMngrResponseSuccessful } from '../utils/fileMngrPath';

// third-party imports

import { useIntl } from 'react-intl';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';
import { useDropzone } from 'react-dropzone';

export const fileMngrHandleUploadFiles = async (
    files = null,
    filesToUpload,
    dispatch,
    intl,
    setIsLoading,
    currentPath,
    uploadUrl,
    handleRefreshFolder,
    handleCloseUploadDialog
) => {
    const droppedFiles = Array.from(files ?? filesToUpload ?? []);

    if (droppedFiles.length === 0) {
        dispatch(
            openSnackbar({
                open: true,
                message: intl.formatMessage({ id: 'ui-comp-filemngr-dialog-uploadfiles-errmsg-nofiles' }),
                variant: 'alert',
                alert: { color: 'error' }
            })
        );
        return;
    }

    if (droppedFiles.some((file) => file?.size > 10000000)) {
        dispatch(
            openSnackbar({
                open: true,
                message: intl.formatMessage({ id: 'ui-comp-filemngr-dialog-uploadfiles-errmsg-filesizetoolarge' }),
                variant: 'alert',
                alert: { color: 'error' }
            })
        );
        return;
    }

    try {
        setIsLoading(true);

        const uploadFiles = droppedFiles;
        const path = currentPath;
        const action = 'save';
        const data = {};
        const fileName = droppedFiles.map((file) => file.name).join(', ');

        const formData = new FormData();
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < uploadFiles.length; i++) {
            formData.append('uploadFiles[]', uploadFiles[i]);
        }
        formData.append('action', action);
        formData.append('path', path);
        formData.append('data', JSON.stringify(data));
        formData.append('fileName', fileName);

        const response = await gestopAPI.post(uploadUrl, formData);
        if (!isFileMngrResponseSuccessful(response)) {
            throw new Error(getFileMngrErrorMessage(response) ?? 'Unable to upload files.');
        }

        if (typeof handleRefreshFolder === 'function') {
            await handleRefreshFolder();
        }

        handleCloseUploadDialog();
    } catch (error) {
        dispatch(
            openSnackbar({
                open: true,
                message: error?.message || intl.formatMessage({ id: 'ui-comp-filemngr-dialog-uploadfiles-errmsg-erruploading' }),
                variant: 'alert',
                alert: { color: 'error' }
            })
        );
    } finally {
        setIsLoading(false);
    }
};

// ==============================|| FILEMNGR DIALOG UPLOAD ||============================== //

/**
 * FileMngrDialogUpload component - Responsible for upload files in the file manager dialog.
 * @author Marcos L. A. Soto <marcosleonildo@gmail.com>
 * @param {Object} props - The component props.
 * @param {boolean} props.openUploadDialog - Flag to control the visibility of the Upload dialog.
 * @param {Function} props.handleCloseUploadDialog - Callback function to handle the close event of the new folder dialog.
 * @param {Function} props.handleRefreshFolder - Callback function to handle the refresh event of the folder.
 * @param {string} props.currentPath - The current path of the folder.
 * @param {string} props.url - The URL for the API.
 * @returns {JSX.Element} The JSX element representing the new folder dialog component.
 */
const FileMngrDialogUpload = ({ openUploadDialog, handleCloseUploadDialog, handleRefreshFolder, currentPath, url }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [filesToUpload, setFilesToUpload] = useState(null);

    const theme = useTheme();
    const intl = useIntl();
    const dispatch = useDispatch();

    const handleDroppedFile = (droppedFiles) => setFilesToUpload(Array.from(droppedFiles ?? []));

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: handleDroppedFile, maxSize: 10000000 });

    const handleRemoveUploadFile = () => setFilesToUpload(null);

    return (
        <FileMngrDialog
            title={intl.formatMessage({ id: 'ui-comp-filemngr-dialog-uploadfiles-title' })}
            isOpen={openUploadDialog}
            onClose={handleCloseUploadDialog}
            buttons={
                filesToUpload?.length > 0
                    ? [
                          {
                              text: intl.formatMessage({ id: 'ui-comp-filemngr-dialog-button-upload' }),
                              onClick: () =>
                                  fileMngrHandleUploadFiles(
                                      null,
                                      filesToUpload,
                                      dispatch,
                                      intl,
                                      setIsLoading,
                                      currentPath,
                                      url,
                                      handleRefreshFolder,
                                      handleCloseUploadDialog
                                  ),
                              variant: 'contained',
                              color: 'warning',
                              disabled: isLoading,
                              isLoading
                          }
                      ]
                    : []
            }
        >
            <Stack spacing={1.5} justifyContent="center" alignItems="center" width={320} height={120} p={1}>
                {!isLoading && filesToUpload === null && (
                    <Stack {...getRootProps()}>
                        <input {...getInputProps()} disabled={isLoading} multiple />
                        <Button
                            sx={{
                                border: `2px dashed ${isDragActive ? '#CCE8FF' : '#CACACA'}`,
                                px: 2.5,
                                color: theme.palette.grey[500]
                            }}
                            size="large"
                            fullWidth
                            disabled={isLoading}
                        >
                            <Stack flexGrow={1} width="100%" justifyContent="center" alignItems="center" borderRadius={2}>
                                <UploadIcon fontSize="medium" />
                                <Typography variant="h5">
                                    {intl.formatMessage({ id: 'ui-comp-filemngr-dialog-uploadfiles-dnd-text' })}
                                </Typography>
                                <Typography>{intl.formatMessage({ id: 'ui-comp-filemngr-dialog-uploadfiles-or' })}</Typography>
                                <Typography variant="contained">
                                    {intl.formatMessage({ id: 'ui-comp-filemngr-dialog-uploadfiles-selectthefile-button-text' })}
                                </Typography>
                            </Stack>
                        </Button>
                    </Stack>
                )}
                {!isLoading && filesToUpload?.length > 0 && (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        flexGrow={1}
                        width="100%"
                        p={1}
                        borderBottom="1px solid #ccc"
                    >
                        <Stack>
                            <Typography variant="subtitle1" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {`${filesToUpload[0]?.name}${filesToUpload.length > 1 ? ` (+${filesToUpload.length - 1})` : ''}`}
                            </Typography>
                            <Typography variant="body2">
                                {fileMngrBytesToSize(filesToUpload.reduce((size, file) => size + (file?.size ?? 0), 0))}
                            </Typography>
                        </Stack>
                        <Button onClick={() => handleRemoveUploadFile()} color="error" size="small">
                            {intl.formatMessage({ id: 'ui-comp-filemngr-dialog-uploadfiles-remove' })}
                        </Button>
                    </Stack>
                )}
                {isLoading && (
                    <Stack flexGrow={1} width="100%" justifyContent="center" alignItems="center">
                        <CircularProgress color="primary" />
                    </Stack>
                )}
            </Stack>
        </FileMngrDialog>
    );
};

FileMngrDialogUpload.propTypes = {
    openUploadDialog: PropTypes.bool,
    handleCloseUploadDialog: PropTypes.func,
    handleRefreshFolder: PropTypes.func,
    currentPath: PropTypes.string,
    url: PropTypes.string
};
export default FileMngrDialogUpload;
